import { useEffect, useRef, useSyncExternalStore } from 'react';

function subscribe(onChange: () => void): () => void {
  window.addEventListener('online', onChange);
  window.addEventListener('offline', onChange);
  return () => {
    window.removeEventListener('online', onChange);
    window.removeEventListener('offline', onChange);
  };
}

/** Whether the browser says it has a connection, re-rendering as that changes. */
export function useOnline(): boolean {
  return useSyncExternalStore(subscribe, () => navigator.onLine);
}

/**
 * Run `retry` when the browser says the connection is back, if `failed` then.
 * Only on that change: a load that fails while the browser thinks it's online
 * waits for the person's "Try again" rather than retrying in a loop.
 */
export function useRetryWhenBackOnline(failed: boolean, retry: () => void): void {
  const latest = useRef({ failed, retry });
  latest.current = { failed, retry };
  useEffect(() => {
    const online = () => {
      if (latest.current.failed) latest.current.retry();
    };
    window.addEventListener('online', online);
    return () => window.removeEventListener('online', online);
  }, []);
}
